import { produce } from "immer";
import { FilterState } from "../../adapters/insights-agent/state/AppState.ts";
// import { contextualRelevancyMapping } from "../../adapters/insights-agent/state/contextualRelevancyMapping.ts";

const monthMapping: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

type ChipValue = {
  label: string;
  removeChipValue: (filterState: FilterState) => FilterState;
};

export const makeChipValues = (filterState: FilterState): ChipValue[] => {
  const chipValues: ChipValue[] = [];
  makeArrayValues(filterState, chipValues);
  makeDateValues(filterState, chipValues);
  // makeContextualRelevancy(filterState, chipValues);
  makeCustomValues(filterState, chipValues);
  return chipValues;
};

function makeCustomValues(
  filterState: FilterState,
  chipValues: ChipValue[],
): void {
  if (filterState.custom_tags_filter) {
    filterState.custom_tags_filter.forEach((customTag) => {
      customTag.values.forEach((value) => {
        chipValues.push({
          label: `${value}`,
          removeChipValue: (filterState: FilterState): FilterState =>
            produce(filterState, (draft) => {
              if (draft.custom_tags_filter) {
                const updatedCustomTags = draft.custom_tags_filter
                  .map((tag) => {
                    if (tag.key === customTag.key) {
                      const filteredValues = tag.values.filter(
                        (v) => v !== value,
                      );
                      return { ...tag, values: filteredValues };
                    }
                    return tag;
                  })
                  .filter((tag) => tag.values.length > 0); // Remove tags with no values
                if (updatedCustomTags.length === 0) {
                  delete draft.custom_tags_filter; // Remove the entire custom_tags_filter if empty
                } else {
                  draft.custom_tags_filter = updatedCustomTags;
                }
              }
            }),
        });
      });
    });
  }
}

// function makeContextualRelevancy(
//   filterState: FilterState,
//   chipValues: ChipValue[],
// ): void {
//   if (filterState.contextual_relevancy) {
//     filterState.contextual_relevancy.forEach((value) => {
//       chipValues.push({
//         label:
//           contextualRelevancyMapping[value] +
//           (value !== "0" ? " relevance" : ""),
//         removeChipValue: (filterState: FilterState): FilterState =>
//           produce(filterState, (draft) => {
//             if (draft.contextual_relevancy) {
//               const filteredValues = draft.contextual_relevancy.filter(
//                 (v) => v !== value,
//               );
//               if (filteredValues.length === 0) {
//                 delete draft.contextual_relevancy;
//               } else {
//                 draft.contextual_relevancy = filteredValues;
//               }
//             }
//           }),
//       });
//     });
//   }
// }

function makeArrayValues(
  filterState: FilterState,
  chipValues: ChipValue[],
): void {
  const excludedKeys = [
    "contextual_relevancy",
    "creation_date",
    "excluded_files",
    "current_turn_additional_exclusions",
    "contextual_relevancy_docs",
  ];
  const filterKeys = Object.keys(filterState).filter(
    (key) => !excludedKeys.includes(key),
  );
  filterKeys.forEach((key) => {
    const values = filterState[key as keyof FilterState];
    if (Array.isArray(values)) {
      values.forEach((value) => {
        if (typeof value !== "string") return;
        chipValues.push({
          label: value,
          removeChipValue: (filterState: FilterState): FilterState =>
            produce(filterState, (draft) => {
              const currentValues = draft[key as keyof FilterState];
              if (Array.isArray(currentValues)) {
                const filteredValues = currentValues.filter((v) => v !== value);
                if (filteredValues.length === 0) {
                  delete draft[key as keyof FilterState];
                } else {
                  // @ts-expect-error type mismatch
                  draft[key as keyof FilterState] =
                    filteredValues as typeof currentValues;
                }
              }
            }),
        });
      });
    }
  });
}

function makeDateValues(filterState: FilterState, chipValues: ChipValue[]) {
  if (filterState.creation_date) {
    const startDate = filterState.creation_date.start;
    const endDate = filterState.creation_date.end;
    const removeChipValue = (filterState: FilterState): FilterState =>
      produce(filterState, (draft) => {
        delete draft.creation_date;
      });
    if (startDate?.year && endDate?.year) {
      const startMonth = monthMapping[startDate.month || "01"];
      const endMonth = monthMapping[endDate.month || "12"];
      chipValues.push({
        label: `${startMonth}${startDate.year}-${endMonth}${endDate.year}`,
        removeChipValue,
      });
    } else if (startDate?.year) {
      const startMonth = monthMapping[startDate.month || "01"];
      chipValues.push({
        label: `Earliest${startMonth}${startDate.year}`,
        removeChipValue,
      });
    } else if (endDate?.year) {
      const endMonth = monthMapping[endDate.month || "12"];
      chipValues.push({
        label: `Latest${endMonth}${endDate.year}`,
        removeChipValue,
      });
    }
  }
}
